import "./StripeUpdatePayment.scss";

import { Alert, Button, ExclamationTriangleIcon, Flex } from "@fluentui/react-northstar";
import FormState, {
  FormData,
  ValidatorDictionary,
  validateRequired,
  validators,
} from "@shopify/react-form-state";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { StripeCardElement, StripeCardElementOptions } from "@stripe/stripe-js";
import { UserDTO } from "@teams/types/api";
import React, { useMemo, useState } from "react";

import { isError } from "../../services/ChannelApi";
import { paymentsApi } from "../../services/PaymentsApi";
import { useTheme } from "../../state/ThemeProvider";
import { clientEventTracker } from "../../utils/clientTelemetry";
import { isValidEmail } from "../../validators";
import { FieldLabel, TextInputField } from "../fields";
import { StripeForm } from "./StripeForm";

type Props = {
  user: UserDTO;
  authToken: string;
  subscriptionId: string;
  onCompleted: () => void;
};

type PaymentInput = {
  name: string;
  email: string;
};

export const StripeUpdatePaymentComponent: React.FC<Props> = ({
  user,
  authToken,
  subscriptionId,
  onCompleted,
}) => {
  const { siteVariables } = useTheme();
  const [error, setError] = useState<string>();
  const elements = useElements();
  const stripe = useStripe();

  const onStripeSubmit = async (formDetails: FormData<PaymentInput>) => {
    setError(undefined);

    if (!elements || !stripe) {
      setError("stripe has not loaded yet");
      return;
    }
    const { fields } = formDetails;

    const cardElement = elements.getElement(CardElement);

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement as StripeCardElement,
      billing_details: {
        name: fields.name.value,
        email: fields.email.value,
      },
    });

    if (error || !paymentMethod) {
      console.error(error);
      setError(error?.message || "Invalid payment method");
      clientEventTracker.track("card update failed", user, {
        subscriptionId: subscriptionId,
      });
      return;
    }

    const updateResult = await paymentsApi.updateStripePayment(
      authToken,
      paymentMethod.id,
      subscriptionId
    );
    if (isError(updateResult)) {
      console.error(updateResult);
      setError(updateResult.message);
      clientEventTracker.track("card update failed", user, {
        subscriptionId: subscriptionId,
      });
      return;
    }
    clientEventTracker.track("card updated", user, {
      subscriptionId: subscriptionId,
    });

    onCompleted();
  };

  const cardOptions = useMemo<StripeCardElementOptions>(() => {
    //NOTE: Any changes you make here need to be tested against theme switching.  Stripe does not react well to font size changes in particular
    return {
      style: {
        base: {
          color: siteVariables.bodyColor,
          fontFamily: siteVariables.bodyFontFamily,
          "::placeholder": {
            color: siteVariables.bodyColor,
          },
        },
      },
    };
  }, [siteVariables]);

  const initialValues: PaymentInput = {
    name: user?.name ?? "",
    email: user?.upn ?? "",
  };

  return (
    <div className="update-payment">
      <FormState
        validateOnSubmit
        initialValues={initialValues}
        onSubmit={onStripeSubmit}
        validators={getValidator()}
      >
        {({ fields, submitting, submit }) => {
          return (
            <Flex column gap="gap.medium">
              <TextInputField
                field={fields.name}
                label="Name"
                defaultValue={user?.name}
                required
                fluid
                autoFocus
                readOnly={submitting}
              />

              <TextInputField
                field={fields.email}
                label="Email"
                required
                fluid
                readOnly={submitting}
              />

              <div>
                <FieldLabel label="Card Details" size="medium" />
                <CardElement options={cardOptions} />
              </div>

              <Button
                className="update-payment__submit"
                data-tname="update-or-submit-payment"
                primary
                loading={submitting}
                disabled={submitting === true}
                content="Update Payment Information"
                onClick={e => {
                  e.preventDefault();
                  clientEventTracker.track("update card clicked", user, {
                    subscriptionId: subscriptionId,
                  });
                  submit();
                }}
              />
            </Flex>
          );
        }}
      </FormState>
      {error && <Alert danger content={error} icon={<ExclamationTriangleIcon />} />}
    </div>
  );
};

export const StripeUpdatePayment: React.FC<Props> = props => {
  return (
    <StripeForm>
      <StripeUpdatePaymentComponent {...props} />
    </StripeForm>
  );
};

function getValidator(): Partial<ValidatorDictionary<PaymentInput>> {
  return {
    name: validators.required("Name is required"),
    email: [validateRequired(isValidEmail, "A valid email is required")],
  };
}
